
import React, { useState, useCallback, useMemo } from 'react';
import { Har, Entry, Page } from './types';
import FileUpload from './components/FileUpload';
import RequestList from './components/RequestList';
import SummaryView from './components/SummaryView';
import RequestDetailView from './components/RequestDetailView';
import ResponseTimeChart from './components/ResponseTimeChart';
import { calculateSummaryMetrics, getPageTimings } from './utils/harUtils';
import { SearchIcon, FileCodeIcon, ChartBarIcon, EyeIcon, InformationCircleIcon, ArrowPathIcon } from './components/Icons';

const App: React.FC = () => {
  const [harData, setHarData] = useState<Har | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'details'>('summary');
  const [filterType, setFilterType] = useState<'all' | 'errors'>('all');

  const handleFileLoad = useCallback(async (file: File) => {
    setIsLoading(true);
    setError(null);
    setHarData(null);
    setSelectedEntry(null);
    setActiveTab('summary');
    setFilterType('all');
    setSearchTerm('');

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      if (!jsonData.log || !jsonData.log.entries) {
        throw new Error('Invalid HAR file structure. Missing "log" or "log.entries".');
      }
      setHarData(jsonData as Har);
    } catch (e) {
      console.error("Error parsing HAR file:", e);
      setError(e instanceof Error ? e.message : "Failed to parse HAR file. Ensure it's a valid JSON HAR file.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSelectEntry = useCallback((entry: Entry) => {
    setSelectedEntry(entry);
    setActiveTab('details');
  }, []);

  const filteredEntries = useMemo(() => {
    if (!harData) return [];
    let entriesToFilter = harData.log.entries;

    if (filterType === 'errors') {
      entriesToFilter = entriesToFilter.filter(entry => entry.response.status >= 400);
    }

    if (searchTerm) {
      return entriesToFilter.filter(entry =>
        entry.request.url.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return entriesToFilter;
  }, [harData, searchTerm, filterType]);

  const summaryMetrics = useMemo(() => {
    if (!harData) return null;
    return calculateSummaryMetrics(harData);
  }, [harData]);

  const pageTimings = useMemo(() => {
    if (!harData || !harData.log.pages || harData.log.pages.length === 0) return null;
    return getPageTimings(harData.log.pages);
  }, [harData]);

  const clearHarData = () => {
    setHarData(null);
    setSelectedEntry(null);
    setSearchTerm('');
    setError(null);
    setActiveTab('summary');
    setFilterType('all');
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const handleShowErrorRequests = useCallback(() => {
    setFilterType('errors');
    setSearchTerm(''); // Clear search term to show all errors, user can then refine
    // setActiveTab('summary'); // Keep summary active or switch if desired
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilterType('all');
    setSearchTerm('');
  }, []);

  const isFilterActive = filterType !== 'all' || searchTerm !== '';

  return (
    <div className="flex flex-col bg-gray-100 text-gray-800">
      {!harData && !isLoading && (
        <div className="flex-grow flex flex-col items-center justify-center p-8">
          <FileUpload onFileLoad={handleFileLoad} />
          {error && <p className="mt-4 text-red-600">{error}</p>}
        </div>
      )}

      {isLoading && (
        <div className="flex-grow flex items-center justify-center p-16">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-violet-600"></div>
          <p className="ml-4 text-xl text-gray-700">Loading HAR file...</p>
        </div>
      )}

      {harData && !isLoading && (
        <div className="ht-data-main-container flex-grow container mx-auto p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Left Column: Request List & Search */}
          <div className="md:col-span-1 bg-white p-4 rounded-lg shadow-lg flex flex-col max-h-[calc(100vh-220px)]">
            <div className="relative mb-2">
              <input
                type="text"
                placeholder="Search requests (URL)..."
                className="ht-search-input w-full pl-10 p-3 bg-gray-50 text-gray-800 rounded-lg border border-gray-300 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none transition-colors duration-300"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search network requests by URL"
              />
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
            {isFilterActive && (
              <button
                onClick={handleClearFilters}
                className="w-full mb-3 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-3 rounded-lg transition-colors duration-300 flex items-center justify-center space-x-1"
                aria-label="Clear current search and filters"
              >
                <ArrowPathIcon className="h-4 w-4" /> 
                <span>Clear Filters / Reset View</span>
              </button>
            )}
            <p className="text-sm text-gray-500 mb-2">
              {filteredEntries.length} of {harData.log.entries.length} requests shown
              {filterType === 'errors' && <span className="ml-1 text-red-600 font-medium">(Filtering HTTP Errors)</span>}
            </p>
            <div className="overflow-y-auto flex-grow" >
              <RequestList
                entries={filteredEntries}
                selectedEntry={selectedEntry}
                onSelectEntry={handleSelectEntry}
              />
            </div>
          </div>

          {/* Right Column: Tabs for Summary/Details */}
          <div className="md:col-span-2 bg-white p-4 rounded-lg shadow-lg overflow-y-auto">
            <div className="mb-4 flex border-b border-gray-300">
              <button
                onClick={() => setActiveTab('summary')}
                className={`py-2 px-4 font-semibold transition-colors duration-300 flex items-center space-x-2 ${activeTab === 'summary' ? 'border-b-2 border-violet-600 text-violet-600' : 'text-gray-500 hover:text-violet-500'}`}
                aria-pressed={activeTab === 'summary'}
              >
                <ChartBarIcon className="h-5 w-5" />
                <span>Summary & Chart</span>
              </button>
              <button
                onClick={() => setActiveTab('details')}
                disabled={!selectedEntry}
                className={`py-2 px-4 font-semibold transition-colors duration-300 flex items-center space-x-2 ${activeTab === 'details' ? 'border-b-2 border-violet-600 text-violet-600' : 'text-gray-500 hover:text-violet-500'} ${!selectedEntry ? 'opacity-50 cursor-not-allowed' : ''}`}
                aria-pressed={activeTab === 'details'}
                aria-disabled={!selectedEntry}
              >
                <EyeIcon className="h-5 w-5" />
                <span>Request Details</span>
              </button>
            </div>

            {activeTab === 'summary' && summaryMetrics && (
              <div className="space-y-6">
                <SummaryView 
                  metrics={summaryMetrics} 
                  pageTimings={pageTimings}
                  onShowErrorRequests={handleShowErrorRequests} 
                />
                <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
                  <h3 className="text-xl font-geomentia mb-3 text-gray-700">Response Time Distribution</h3>
                  <ResponseTimeChart entries={harData.log.entries} />
                </div>
              </div>
            )}

            {activeTab === 'details' && selectedEntry && (
              <RequestDetailView entry={selectedEntry} />
            )}
            {activeTab === 'details' && !selectedEntry && (
                 <div className="text-center py-10 text-gray-500">
                    <InformationCircleIcon className="h-10 w-10 mx-auto mb-2 text-violet-500"/>
                    <p>Select a request from the list to view its details.</p>
                </div>
            )}
          </div>
        </div>
      )}
       {error && !isLoading && harData && (
        <div className="container mx-auto p-4">
            <p className="mt-4 text-red-700 bg-red-100 p-3 rounded-md border border-red-300">{error}</p>
        </div>
       )}
      {harData && (
          <div className="bg-white border shadow-xl/20 p-3 sticky top-0 z-50">
            <div className="container mx-auto flex items-center justify-center">
              <button
                  onClick={clearHarData}
                  className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-300 text-sm flex items-center space-x-2"
                  aria-label="Clear all loaded HAR data"
              >
                <span>Clear Data</span>
              </button>
            </div>
          </div>
      )}
    </div>
  );
};

export default App;
